import type { MenuState, MenuTab, MenuItem } from './types';

export type MenuAction =
  | { type: 'ADD_TAB'; tabId: string; name: string }
  | { type: 'RENAME_TAB'; tabId: string; name: string }
  | { type: 'ARCHIVE_TAB'; tabId: string }
  | { type: 'RESTORE_TAB'; tabId: string }
  | { type: 'REORDER_TABS'; tabIds: string[] }
  | { type: 'ADD_MENU_ITEM'; tabId: string; itemId: string; name: string; price: number; color: string }
  | { type: 'UPDATE_MENU_ITEM'; itemId: string; name?: string; price?: number; color?: string; tabId?: string }
  | { type: 'ARCHIVE_MENU_ITEM'; itemId: string }
  | { type: 'RESTORE_MENU_ITEM'; itemId: string }
  | { type: 'REORDER_ITEMS'; tabId: string; itemIds: string[] };

function reorderById<T extends { id: string }>(list: T[], ids: string[]): T[] {
  const byId = new Map(list.map((entry) => [entry.id, entry]));
  const ordered = ids.map((id) => byId.get(id)).filter((entry): entry is T => entry !== undefined);
  const orderedIds = new Set(ordered.map((entry) => entry.id));
  const leftovers = list.filter((entry) => !orderedIds.has(entry.id));
  return [...ordered, ...leftovers];
}

function findItemTabIndex(tabs: MenuTab[], itemId: string): number {
  return tabs.findIndex((tab) => tab.items.some((item) => item.id === itemId));
}

export function menuReducer(state: MenuState, action: MenuAction): MenuState {
  switch (action.type) {
    case 'ADD_TAB': {
      const tab: MenuTab = { id: action.tabId, name: action.name, items: [] };
      return { ...state, tabs: [...state.tabs, tab] };
    }

    case 'RENAME_TAB': {
      const tabs = state.tabs.map((tab) =>
        tab.id === action.tabId ? { ...tab, name: action.name } : tab
      );
      return { ...state, tabs };
    }

    case 'ARCHIVE_TAB': {
      const tabs = state.tabs.map((tab) =>
        tab.id === action.tabId ? { ...tab, archived: true } : tab
      );
      return { ...state, tabs };
    }

    case 'RESTORE_TAB': {
      const tabs = state.tabs.map((tab) =>
        tab.id === action.tabId ? { ...tab, archived: false } : tab
      );
      return { ...state, tabs };
    }

    case 'REORDER_TABS': {
      return { ...state, tabs: reorderById(state.tabs, action.tabIds) };
    }

    case 'ADD_MENU_ITEM': {
      const item: MenuItem = {
        id: action.itemId,
        name: action.name,
        price: action.price,
        color: action.color,
      };
      const tabs = state.tabs.map((tab) =>
        tab.id === action.tabId ? { ...tab, items: [...tab.items, item] } : tab
      );
      return { ...state, tabs };
    }

    case 'UPDATE_MENU_ITEM': {
      const fromTabIdx = findItemTabIndex(state.tabs, action.itemId);
      if (fromTabIdx === -1) return state;
      const fromTab = state.tabs[fromTabIdx];
      const current = fromTab.items.find((item) => item.id === action.itemId)!;
      const updated: MenuItem = {
        ...current,
        ...(action.name !== undefined && { name: action.name }),
        ...(action.price !== undefined && { price: action.price }),
        ...(action.color !== undefined && { color: action.color }),
      };

      if (action.tabId === undefined || action.tabId === fromTab.id) {
        const tabs = state.tabs.map((tab, idx) =>
          idx === fromTabIdx
            ? { ...tab, items: tab.items.map((item) => (item.id === action.itemId ? updated : item)) }
            : tab
        );
        return { ...state, tabs };
      }

      const tabs = state.tabs.map((tab) => {
        if (tab.id === fromTab.id) {
          return { ...tab, items: tab.items.filter((item) => item.id !== action.itemId) };
        }
        if (tab.id === action.tabId) {
          return { ...tab, items: [...tab.items, updated] };
        }
        return tab;
      });
      return { ...state, tabs };
    }

    case 'ARCHIVE_MENU_ITEM': {
      const tabs = state.tabs.map((tab) => ({
        ...tab,
        items: tab.items.map((item) => (item.id === action.itemId ? { ...item, archived: true } : item)),
      }));
      return { ...state, tabs };
    }

    case 'RESTORE_MENU_ITEM': {
      const tabs = state.tabs.map((tab) => ({
        ...tab,
        items: tab.items.map((item) => (item.id === action.itemId ? { ...item, archived: false } : item)),
      }));
      return { ...state, tabs };
    }

    case 'REORDER_ITEMS': {
      const tabs = state.tabs.map((tab) =>
        tab.id === action.tabId ? { ...tab, items: reorderById(tab.items, action.itemIds) } : tab
      );
      return { ...state, tabs };
    }

    default:
      return state;
  }
}
